import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Card, Input, Loader, Stack, Text } from '@wapharma/design-system';
import { resetPassword, validatePasswordResetToken } from '../auth/passwordReset';
import {
  getStoredPasswordResetEmail,
  passwordResetToastMessage,
  submitPasswordResetEmail,
} from '../auth/password-reset-email';
import { AdminAuthLayout } from '../components/auth/AdminAuthLayout';
import authStyles from '../components/auth/auth-layout.module.css';
import { PasswordInput } from '../components/PasswordInput';
import { useToast } from '../contexts/ToastContext';
import { getDisplayError } from '../apiErrors';

type TokenStatus = 'checking' | 'valid' | 'invalid';

export function ResetPasswordPage() {
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const year = new Date().getFullYear();

  const [tokenStatus, setTokenStatus] = useState<TokenStatus>(() =>
    token && /^\d{6}$/.test(token) ? 'checking' : 'invalid',
  );
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fieldError, setFieldError] = useState<string | undefined>();
  const [pending, setPending] = useState(false);
  const [resendEmail, setResendEmail] = useState(() => getStoredPasswordResetEmail());
  const [resending, setResending] = useState(false);

  useEffect(() => {
    if (!token || !/^\d{6}$/.test(token)) {
      setTokenStatus('invalid');
      return;
    }

    let cancelled = false;
    setTokenStatus('checking');

    void (async () => {
      try {
        await validatePasswordResetToken(token);
        if (!cancelled) setTokenStatus('valid');
      } catch {
        if (!cancelled) setTokenStatus('invalid');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token]);

  const submitNewPassword = async (e: FormEvent) => {
    e.preventDefault();
    setFieldError(undefined);
    const normalizedPassword = password.trim();
    const normalizedConfirmPassword = confirmPassword.trim();

    if (tokenStatus !== 'valid') {
      showToast('Lien ou code invalide. Demandez un nouveau code.', { tone: 'error' });
      return;
    }
    if (normalizedPassword.length < 8) {
      setFieldError('Le mot de passe doit contenir au moins 8 caractères.');
      return;
    }
    if (normalizedPassword !== normalizedConfirmPassword) {
      setFieldError('Les mots de passe ne correspondent pas.');
      return;
    }

    setPending(true);
    try {
      await resetPassword(token, normalizedPassword);
      showToast('Mot de passe mis à jour. Connectez-vous.', { tone: 'success' });
      navigate('/login', { replace: true });
    } catch (err: unknown) {
      showToast(getDisplayError(err), { tone: 'error' });
    } finally {
      setPending(false);
    }
  };

  const submitResend = async (e: FormEvent) => {
    e.preventDefault();
    if (!resendEmail.trim()) {
      showToast('Indiquez votre adresse e-mail.', { tone: 'error' });
      return;
    }

    setResending(true);
    try {
      const result = await submitPasswordResetEmail(resendEmail);
      showToast(passwordResetToastMessage(result), {
        tone: result === 'throttled' ? 'error' : 'success',
      });
      if (result === 'sent') {
        navigate('/forgot-password', { replace: true });
      }
    } catch (err: unknown) {
      showToast(getDisplayError(err), { tone: 'error' });
    } finally {
      setResending(false);
    }
  };

  return (
    <AdminAuthLayout
      brandTitle="Nouveau mot de passe"
      brandBody="Choisissez un mot de passe robuste pour sécuriser votre accès à l'administration."
    >
      <Stack gap="xl">
        <Card elevation="flat" padding="lg" className={authStyles.authFormCard}>
          <Stack gap={0}>
            {tokenStatus === 'checking' ? (
              <Stack gap="md" align="center" className={authStyles.authStepBody}>
                <Loader />
                <Text size="sm" c="dimmed" ta="center" ff="body">
                  Vérification du code…
                </Text>
              </Stack>
            ) : null}

            {tokenStatus === 'invalid' ? (
              <>
                <div className={authStyles.formHeaderBand}>
                  <div className={authStyles.formHeading}>
                    <Text ff="display" size="lg" fw={500} c="primary" component="div">
                      Lien expiré ou invalide
                    </Text>
                    <Text size="sm" c="dimmed" ff="body">
                      Demandez un nouveau code pour réinitialiser votre mot de passe.
                    </Text>
                  </div>
                </div>

                <div className={authStyles.authStepBody}>
                <form onSubmit={(e) => void submitResend(e)} noValidate>
                  <Stack gap="md">
                    <Input
                      label="E-mail"
                      name="email"
                      type="email"
                      autoComplete="email"
                      placeholder="vous@exemple.com"
                      value={resendEmail}
                      onChange={(ev) => setResendEmail(ev.target.value)}
                    />
                    <Button type="submit" disabled={resending} className={authStyles.submitBtn}>
                      {resending ? 'Envoi…' : 'Recevoir un nouveau code'}
                    </Button>
                  </Stack>
                </form>

                <div className={authStyles.authSecondaryActions}>
                  <Link to="/forgot-password" className={authStyles.authNavLink}>
                    Saisir le code reçu
                  </Link>
                  <span className={authStyles.authLinkSeparator} aria-hidden>
                    ·
                  </span>
                  <Link to="/login" className={authStyles.authNavLink}>
                    Retour à la connexion
                  </Link>
                </div>
                </div>
              </>
            ) : null}

            {tokenStatus === 'valid' ? (
              <>
                <div className={authStyles.formHeaderBand}>
                  <div className={authStyles.formHeading}>
                    <Text ff="display" size="lg" fw={500} c="primary" component="div">
                      Définir un nouveau mot de passe
                    </Text>
                    <Text size="sm" c="dimmed" ff="body">
                      Au moins 8 caractères. Évitez de réutiliser un ancien mot de passe.
                    </Text>
                  </div>
                </div>

                <div className={authStyles.authStepBody}>
                <form onSubmit={(e) => void submitNewPassword(e)} noValidate>
                  <Stack gap="md">
                    <PasswordInput
                      label="Nouveau mot de passe"
                      name="password"
                      autoComplete="new-password"
                      placeholder="Au moins 8 caractères"
                      value={password}
                      onChange={(ev) => setPassword(ev.target.value)}
                    />
                    <PasswordInput
                      label="Confirmer le mot de passe"
                      name="confirmPassword"
                      autoComplete="new-password"
                      placeholder="Répétez le mot de passe"
                      value={confirmPassword}
                      error={fieldError}
                      onChange={(ev) => setConfirmPassword(ev.target.value)}
                    />
                    <Button type="submit" disabled={pending} className={authStyles.submitBtn}>
                      {pending ? 'Enregistrement…' : 'Enregistrer le mot de passe'}
                    </Button>
                  </Stack>
                </form>

                <div className={authStyles.authFormFooter}>
                  <Link to="/login" className={authStyles.authNavLink}>
                    Retour à la connexion
                  </Link>
                </div>
                </div>
              </>
            ) : null}
          </Stack>
        </Card>

        <Text size="xs" c="dimmed" ta="center" ff="body" className={authStyles.mobileFooter}>
          © {year} W.A. Pharma · Réservé au personnel autorisé
        </Text>
      </Stack>
    </AdminAuthLayout>
  );
}
